import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  History,
  Search,
  ChevronLeft,
  ChevronRight,
  Eye,
  RefreshCw,
} from 'lucide-react'
import { auditLogApi } from '@/services/api'
import { cn } from '@/lib/utils'
import { ErrorState } from '@/components/common/States'
import { Badge, type BadgeProps } from '@/components/common/Badge'

// ============================================================
// Types
// ============================================================

interface AuditLogEntry {
  id: string
  action: string
  entity: string
  entityId: string
  userId: string
  user?: {
    firstName?: string
    lastName?: string
    email?: string
  }
  changes?: unknown
  source?: string
  ipAddress?: string
  createdAt: string
}

interface Pagination {
  page: number
  limit: number
  total: number
  totalPages: number
  hasNext: boolean
  hasPrev: boolean
}

interface AuditLogResponse {
  data: AuditLogEntry[]
  pagination: Pagination
}

// ============================================================
// Constants
// ============================================================

const ENTITY_OPTIONS = [
  { value: '', label: 'Tutti' },
  { value: 'client', label: 'Client' },
  { value: 'project', label: 'Project' },
  { value: 'task', label: 'Task' },
  { value: 'content', label: 'Content' },
  { value: 'campaign', label: 'Campaign' },
  { value: 'event', label: 'Event' },
  { value: 'user', label: 'User' },
]

const ACTION_BADGE_VARIANT: Record<string, BadgeProps['variant']> = {
  create: 'success',
  update: 'info',
  delete: 'danger',
}

function getActionBadgeVariant(action: string): BadgeProps['variant'] {
  return ACTION_BADGE_VARIANT[action.toLowerCase()] ?? 'neutral'
}

/** Restituisce una finestra di numeri di pagina centrata sulla pagina corrente. */
function getPageWindow(current: number, totalPages: number, size = 5): number[] {
  if (totalPages <= size) {
    return Array.from({ length: totalPages }, (_, i) => i + 1)
  }
  const half = Math.floor(size / 2)
  let start = Math.max(1, current - half)
  const end = Math.min(totalPages, start + size - 1)
  start = Math.max(1, end - size + 1)
  return Array.from({ length: end - start + 1 }, (_, i) => start + i)
}

function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso))
  } catch {
    return iso
  }
}

function formatDateFull(iso: string): string {
  try {
    return new Intl.DateTimeFormat('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).format(new Date(iso))
  } catch {
    return iso
  }
}

function getUserLabel(entry: AuditLogEntry): string {
  if (entry.user?.firstName && entry.user?.lastName) {
    return `${entry.user.firstName} ${entry.user.lastName}`
  }
  if (entry.user?.email) return entry.user.email
  return entry.userId
}

function renderChanges(changes: unknown): React.ReactNode {
  if (changes === null || changes === undefined) {
    return <span className="text-slate-400 italic text-xs">Nessun dettaglio</span>
  }
  if (typeof changes === 'object' && !Array.isArray(changes)) {
    const entries = Object.entries(changes as Record<string, unknown>)
    if (entries.length === 0) {
      return <span className="text-slate-400 italic text-xs">Oggetto vuoto</span>
    }
    return (
      <dl className="space-y-1">
        {entries.map(([key, value]) => (
          <div key={key} className="flex gap-2 text-xs">
            <dt className="font-medium text-slate-600 min-w-[120px] flex-shrink-0">{key}</dt>
            <dd className="text-slate-800 break-all">
              {typeof value === 'object' ? JSON.stringify(value) : String(value ?? '')}
            </dd>
          </div>
        ))}
      </dl>
    )
  }
  return (
    <pre className="text-xs text-slate-700 whitespace-pre-wrap break-all">
      {JSON.stringify(changes, null, 2)}
    </pre>
  )
}

// ============================================================
// Skeleton row
// ============================================================

function SkeletonRow() {
  return (
    <div className="grid grid-cols-[160px_1fr_120px_100px_130px_60px] gap-3 px-4 py-3 items-center border-b border-slate-200">
      <div className="h-3 bg-slate-100 rounded animate-pulse w-28" />
      <div className="h-3 bg-slate-100 rounded animate-pulse w-32" />
      <div className="h-5 bg-slate-100 rounded-full animate-pulse w-16" />
      <div className="h-3 bg-slate-100 rounded animate-pulse w-20" />
      <div className="h-3 bg-slate-100 rounded animate-pulse w-24" />
      <div className="h-6 w-6 bg-slate-100 rounded animate-pulse ml-auto" />
    </div>
  )
}

// ============================================================
// ExpandedRow
// ============================================================

function ExpandedRow({ entry }: { entry: AuditLogEntry }) {
  return (
    <div className="px-6 py-5 bg-slate-50 border-b border-slate-200 border-l-2 border-l-primary-500">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h4 className="text-xs font-semibold text-subtle uppercase tracking-wide mb-2">Modifiche</h4>
          {renderChanges(entry.changes)}
        </div>
        <div className="space-y-3">
          <div>
            <span className="text-xs font-semibold text-subtle uppercase tracking-wide">Timestamp completo</span>
            <p className="text-xs text-slate-700 mt-0.5 tabular-nums">{formatDateFull(entry.createdAt)}</p>
          </div>
          {entry.source && (
            <div>
              <span className="text-xs font-semibold text-subtle uppercase tracking-wide">Sorgente</span>
              <p className="text-xs text-slate-700 mt-0.5">{entry.source}</p>
            </div>
          )}
          {entry.ipAddress && (
            <div>
              <span className="text-xs font-semibold text-subtle uppercase tracking-wide">Indirizzo IP</span>
              <p className="text-xs text-slate-700 font-mono mt-0.5 tabular-nums">{entry.ipAddress}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ============================================================
// AuditTrailPage
// ============================================================

interface Filters {
  entity: string
  action: string
  startDate: string
  endDate: string
}

const EMPTY_FILTERS: Filters = {
  entity: '',
  action: '',
  startDate: '',
  endDate: '',
}

export function AuditTrailPage() {
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS)
  const [pendingFilters, setPendingFilters] = useState<Filters>(EMPTY_FILTERS)
  const [page, setPage] = useState(1)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const limit = 20

  const queryParams: Record<string, unknown> = {
    page,
    limit,
    ...(filters.entity ? { entity: filters.entity } : {}),
    ...(filters.action ? { action: filters.action } : {}),
    ...(filters.startDate ? { startDate: filters.startDate } : {}),
    ...(filters.endDate ? { endDate: filters.endDate } : {}),
  }

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['audit-log', queryParams],
    queryFn: () => auditLogApi.list(queryParams),
    retry: false,
    placeholderData: (prev) => prev,
  })

  // Il registro risponde `{ data: { data, pagination } }` senza la chiave `success`,
  // quindi l'interceptor di axios non toglie l'involucro come fa per le altre rotte:
  // qui arriva un livello in più. Leggere solo `response.data` restituiva un oggetto
  // al posto dell'elenco, e la pagina restava vuota senza dire niente.
  const raw = data as { data?: unknown; pagination?: unknown } | undefined
  const response = ((raw as any)?.data?.data ? (raw as any).data : raw) as AuditLogResponse | undefined
  const logs: AuditLogEntry[] = Array.isArray(response?.data) ? response.data : []
  const pagination = response?.pagination

  function applyFilters() {
    setFilters(pendingFilters)
    setPage(1)
    setExpandedId(null)
  }

  function resetFilters() {
    setPendingFilters(EMPTY_FILTERS)
    setFilters(EMPTY_FILTERS)
    setPage(1)
    setExpandedId(null)
  }

  function setPending(field: keyof Filters) {
    return (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setPendingFilters((p) => ({ ...p, [field]: e.target.value }))
  }

  function toggleExpand(id: string) {
    setExpandedId((prev) => (prev === id ? null : id))
  }

  const totalPages = pagination?.totalPages ?? 1
  const total = pagination?.total ?? 0

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-slate-100 text-slate-500 flex-shrink-0">
            <History className="w-5 h-5" />
          </span>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Audit Trail</h1>
            <p className="text-sm text-slate-500 mt-0.5">Registro attività e modifiche del sistema</p>
          </div>
        </div>
        <button
          onClick={() => refetch()}
          title="Aggiorna"
          className="inline-flex items-center justify-center w-9 h-9 text-slate-500 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Filter bar */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-card px-4 py-3">
        <div className="flex items-center gap-2.5 flex-wrap">
          {/* Entity dropdown */}
          <select
            value={pendingFilters.entity}
            onChange={setPending('entity')}
            className="px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/40 min-w-[120px]"
          >
            {ENTITY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          {/* Action text input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Azione..."
              value={pendingFilters.action}
              onChange={setPending('action')}
              className="pl-9 pr-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/40 placeholder:text-slate-400 w-36"
            />
          </div>

          {/* Date range */}
          <input
            type="date"
            value={pendingFilters.startDate}
            onChange={setPending('startDate')}
            className="px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/40 tabular-nums"
          />
          <span className="text-xs text-slate-400">—</span>
          <input
            type="date"
            value={pendingFilters.endDate}
            onChange={setPending('endDate')}
            className="px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/40 tabular-nums"
          />

          {/* Actions */}
          <button
            onClick={applyFilters}
            className="px-3.5 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
          >
            Filtra
          </button>
          <button
            onClick={resetFilters}
            className="px-3.5 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
          >
            Reset
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-card overflow-hidden">
        {/* Error state */}
        {!isLoading && isError && (
          <ErrorState
            title="Impossibile caricare l'audit trail"
            description="Si è verificato un errore durante il caricamento dei log. Riprova."
            onRetry={() => refetch()}
          />
        )}

        {/* Empty state */}
        {!isLoading && !isError && logs.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="flex items-center justify-center w-12 h-12 bg-slate-100 rounded-full mb-4">
              <History className="w-6 h-6 text-slate-400" />
            </div>
            <h3 className="text-sm font-medium text-slate-700 mb-1">Nessun log trovato</h3>
            <p className="text-sm text-slate-500">
              {filters.entity || filters.action || filters.startDate || filters.endDate
                ? 'Prova a modificare i filtri di ricerca'
                : 'Non ci sono ancora attività registrate'}
            </p>
          </div>
        )}

        {/* Scrollable table region (header + skeleton/rows) */}
        {(isLoading || (!isError && logs.length > 0)) && (
          <div className="overflow-x-auto">
            <div className="min-w-[760px]">
              {/* Table header */}
              <div className="grid grid-cols-[160px_1fr_120px_100px_130px_60px] gap-3 px-4 py-2.5 border-b border-slate-200 bg-slate-50">
                <span className="text-xs font-medium text-subtle uppercase tracking-wide">Data/Ora</span>
                <span className="text-xs font-medium text-subtle uppercase tracking-wide">Utente</span>
                <span className="text-xs font-medium text-subtle uppercase tracking-wide">Azione</span>
                <span className="text-xs font-medium text-subtle uppercase tracking-wide">Entità</span>
                <span className="text-xs font-medium text-subtle uppercase tracking-wide">ID Entità</span>
                <span className="text-xs font-medium text-subtle uppercase tracking-wide text-right">Dettagli</span>
              </div>

              {/* Loading skeleton */}
              {isLoading && (
                <div>
                  {Array.from({ length: 8 }).map((_, i) => (
                    <SkeletonRow key={i} />
                  ))}
                </div>
              )}

              {/* Table rows */}
              {!isLoading && logs.length > 0 && (
                <div>
                  {logs.map((entry) => {
                    const isExpanded = expandedId === entry.id
                    return (
                      <div key={entry.id}>
                        <div
                          className={cn(
                            'grid grid-cols-[160px_1fr_120px_100px_130px_60px] gap-3 px-4 py-3 border-b border-slate-200 last:border-b-0 hover:bg-slate-50 transition-colors items-center',
                            isExpanded && 'bg-slate-50 border-l-2 border-l-primary-500'
                          )}
                        >
                          {/* Data/Ora */}
                          <span className="text-xs text-slate-500 whitespace-nowrap tabular-nums">
                            {formatDate(entry.createdAt)}
                          </span>

                          {/* Utente */}
                          <span className="text-sm text-slate-700 truncate">{getUserLabel(entry)}</span>

                          {/* Azione */}
                          <div>
                            <Badge variant={getActionBadgeVariant(entry.action)} dot>
                              {entry.action}
                            </Badge>
                          </div>

                          {/* Entità */}
                          <span className="text-sm text-slate-600 truncate">{entry.entity}</span>

                          {/* ID Entità */}
                          <span className="text-xs text-slate-400 font-mono truncate tabular-nums" title={entry.entityId}>
                            {entry.entityId}
                          </span>

                          {/* Dettagli */}
                          <div className="flex justify-end">
                            <button
                              onClick={() => toggleExpand(entry.id)}
                              aria-label={isExpanded ? 'Chiudi dettagli' : 'Mostra dettagli'}
                              title={isExpanded ? 'Chiudi dettagli' : 'Mostra dettagli'}
                              className={cn(
                                'p-1.5 rounded-md transition-colors',
                                isExpanded
                                  ? 'text-primary-600 bg-primary-50'
                                  : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
                              )}
                            >
                              <Eye className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>

                        {/* Expanded details */}
                        {isExpanded && <ExpandedRow entry={entry} />}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Pagination */}
        {!isLoading && !isError && totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
            <p className="text-xs text-slate-500 tabular-nums">
              Pagina {page} di {totalPages} ({total} log)
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={!pagination?.hasPrev && page === 1}
                aria-label="Pagina precedente"
                className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-white border border-transparent hover:border-slate-200 rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              {getPageWindow(page, totalPages).map((pageNum) => (
                <button
                  key={pageNum}
                  onClick={() => setPage(pageNum)}
                  aria-label={`Pagina ${pageNum}`}
                  aria-current={page === pageNum ? 'page' : undefined}
                  className={cn(
                    'w-7 h-7 text-xs rounded-md transition-colors tabular-nums',
                    page === pageNum
                      ? 'bg-primary-600 text-white font-medium'
                      : 'text-slate-600 hover:bg-white hover:border hover:border-slate-200'
                  )}
                >
                  {pageNum}
                </button>
              ))}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={!pagination?.hasNext && page === totalPages}
                aria-label="Pagina successiva"
                className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-white border border-transparent hover:border-slate-200 rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
